import { produce } from 'immer';
import { ReactElement, useCallback, useContext, useEffect, useRef } from 'react';

import { fullscreenModalContext } from '@context/Contexts';
import { FullscreenModalState } from '@typing/State';

const useFullscreenModal = (name: string) => {
  const { fullscreenModalState, setFullscreenModalState } = useContext(fullscreenModalContext);

  const onDidDismiss = useRef<() => void>();
  const onWillDismiss = useRef<() => void>();

  const myModalId = useRef<string>();
  const currentState = useRef<FullscreenModalState>(fullscreenModalState);

  useEffect(() => {
    currentState.current = fullscreenModalState;
  }, [fullscreenModalState]);

  useEffect(
    () => () => {
      if (myModalId.current) {
        const newState = produce(currentState.current, draftState => {
          const myIndex = draftState.modals.findIndex(modal => modal.id === myModalId.current);
          if (myIndex >= 0) {
            draftState.modals.splice(myIndex, 1);
          }
        });
        setFullscreenModalState(newState);
        myModalId.current = undefined;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  type PresentArgs = {
    content: ReactElement;
    onDidDismiss?: () => void;
    onDidPresent?: () => void;
    onWillDismiss?: () => void;
    onWillPresent?: () => void;
  };

  const present = useCallback(
    (args: PresentArgs) => {
      if (!myModalId.current) {
        const modalId = Math.random().toString();
        const modal = {
          content: args.content,
          id: modalId,
          isOpen: true,
          name,
          renderCount: 0
        };
        setFullscreenModalState(
          produce(fullscreenModalState, draftState => {
            draftState.modals.push(modal);
          })
        );
        myModalId.current = modalId;
        args.onWillPresent?.();

        setTimeout(() => {
          args.onDidPresent?.();
        }, 100);
      } else {
        const newState = produce(fullscreenModalState, draftState => {
          const mine = draftState.modals.find(modal => modal.id === myModalId.current);
          if (mine) {
            mine.content = args.content;
            mine.isOpen = true;
          }
        });

        setFullscreenModalState(newState);
      }

      onDidDismiss.current = args.onDidDismiss;
      onWillDismiss.current = args.onWillDismiss;
    },
    [fullscreenModalState, name, setFullscreenModalState]
  );

  const dismiss = useCallback(() => {
    if (myModalId.current) {
      setFullscreenModalState(
        produce(fullscreenModalState, draftState => {
          const mine = draftState.modals.find(modal => modal.id === myModalId.current);
          if (mine) {
            mine.isOpen = false;
          }
        })
      );

      setTimeout(() => {
        onDidDismiss.current?.();
        onDidDismiss.current = undefined;
      }, 100);

      onWillDismiss.current?.();
      onWillDismiss.current = undefined;
    }
  }, [fullscreenModalState, onDidDismiss, onWillDismiss, setFullscreenModalState]);

  return { dismiss, present };
};

export default useFullscreenModal;
